import { createFileRoute, notFound } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { SiteFooter } from "@/components/SiteFooter";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import { MASTER_KEYWORDS_TOP as MASTER_KEYWORDS_ALL } from "@/content/master-corpus/top.gen";
import type { BusinessFaqItem } from "@/content/business-corpus/faq-th";
import { BUSINESS_FAQ_CATEGORIES, getBusinessCategoryDef } from "@/content/business-corpus/faq-defs";

export const Route = createFileRoute("/en/business/faq/$category")({
  loader: async ({ params }) => {
    const { getBusinessFaqsByCategoryEn } = await import("@/content/business-corpus/categories");
    const def = getBusinessCategoryDef(params.category);
    if (!def) throw notFound();
    return { def, faqs: getBusinessFaqsByCategoryEn(def.slug) };
  },
  component: BusinessFaqCategoryPageEn,
  head: ({ params, loaderData }) => {
    if (!loaderData) {
      return { meta: [{ title: "Category not found" }, { name: "robots", content: "noindex" }] };
    }
    const site = getActiveSiteUrl();
    const { def, faqs } = loaderData;
    return {
      ...pageHeadForProvince({
        title: `${def.titleEn} — Business FAQ Thailand 2026 (${faqs.length} answers)`,
        description: def.descEn,
        path: `/en/business/faq/${params.category}`,
        type: "article",
        alternates: [{ lang: "th", path: `/business/faq/${params.category}` }],
        keywords: MASTER_KEYWORDS_ALL.slice(0, 60),
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "Home", url: `${site}/en` },
              { name: "Business FAQ", url: `${site}/en/business/faq` },
              { name: def.titleEn, url: `${site}/en/business/faq/${params.category}` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            inLanguage: "en",
            speakable: { "@type": "SpeakableSpecification", cssSelector: ["h1", "h2", ".faq-answer"] },
          }),
        },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema(`/en/business/faq/${params.category}`)) },
      ],
    };
  },
});

function BusinessFaqCategoryPageEn() {
  const { def, faqs } = Route.useLoaderData();
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/en" className="hover:underline">Home</a> <span>/</span>{" "}
          <a href="/en/business/faq" className="hover:underline">Business FAQ</a> <span>/</span> <span>{def.titleEn}</span>
        </nav>
        <h1 className="text-3xl md:text-4xl font-bold mb-3">{def.titleEn}</h1>
        <p className="text-muted-foreground mb-6">{def.descEn}</p>
        <p className="text-sm text-muted-foreground mb-8">
          <strong>{faqs.length}</strong> answers in this category — written by our attorneys, updated for 2026.{" "}
          <a href={`/business/faq/${def.slug}`} className="text-primary underline">ภาษาไทย →</a>
        </p>

        <div className="mb-8 flex flex-wrap gap-2 text-xs">
          {BUSINESS_FAQ_CATEGORIES.map((c) => (
            <a
              key={c.slug}
              href={`/en/business/faq/${c.slug}`}
              className={`rounded-full border px-3 py-1 ${c.slug === def.slug ? "bg-primary text-primary-foreground" : "bg-muted/40 text-muted-foreground hover:bg-muted"}`}
            >
              {c.titleEn}
            </a>
          ))}
        </div>

        <div className="space-y-4">
          {faqs.map((item: BusinessFaqItem, i: number) => (
            <details key={i} className="rounded-lg border bg-card p-4 open:shadow-sm" {...(i < 5 ? { open: true } : {})}>
              <summary className="cursor-pointer font-semibold">
                <span className="text-muted-foreground mr-2">Q{i + 1}.</span>{item.q}
              </summary>
              <p className="faq-answer mt-3 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
            </details>
          ))}
          {faqs.length === 0 && (
            <p className="rounded-lg border bg-muted/30 p-6 text-sm text-muted-foreground">
              No answers here yet — <a href="/contact" className="text-primary underline">ask our team</a>.
            </p>
          )}
        </div>

        <section className="mt-12 rounded-lg border p-6 bg-primary/5">
          <h2 className="text-xl font-bold mb-2">Need this handled end to end?</h2>
          <p className="text-sm text-muted-foreground mb-4">
            Pre-check → certified translation → attorney certification / MFA legalisation → Apostille 2026 →
            DBD, BOI and Immigration filings, with an interpreter at every appointment.
          </p>
          <a href="/contact" className="inline-block rounded-md bg-primary px-5 py-2 text-primary-foreground font-medium">
            Talk to our corporate desk →
          </a>
        </section>
      
        <FaqSourceNote topic="business" locale="en" />
      </main>
      <SiteFooter />
    </>
  );
}
